// PropertyThumb — small reusable cover thumbnail for the booking
// experience (dropdown, per-property blocks, enquiry summary,
// visual property carousel).
//
// Resolves the CMS-managed `hotel/<slug>/about-hotel` slot (the same
// hero used on /hotels/<slug>) and falls back to the static
// HOTELS[*].image cover. Uses SmartImage so it lazy-loads, shows a
// warm shimmer, and gracefully handles broken URLs.
//
// GLOBAL WIDGET IMAGE SYNC:
//   When `cmsSlot` is provided, the component ALSO subscribes to that
//   secondary CMS slot AND stamps `data-cms-slot=<cmsSlot>` on the
//   underlying <img>. That gives the CMS's AutoImageEditor a stable,
//   PAGE-INDEPENDENT id to attach edits to — so a single edit made
//   inside the booking widget on one page (homepage, hotel-detail,
//   etc.) propagates to EVERY other place where the booking widget
//   is embedded. The default hotel hero slot is used as a fallback
//   when no widget-specific override has been persisted yet.
//
//   Lookup order (first non-empty wins):
//     1. cmsSlot override (widget-scoped, global across widget instances)
//     2. hotel/<slug>/about-hotel   (the hotel-detail hero slot)
//     3. HOTELS[*].image             (static fallback)
import { useEffect, useState } from "react";
import { HOTELS } from "@/lib/data";
import {
  ensureImageLoaded, subscribeImage, getCachedImage, resolveUrl,
} from "@/lib/slotCache";
import SmartImage from "@/components/cms/SmartImage";

const readSlot = (slot) => {
  if (!slot) return null;
  const data = getCachedImage(slot);
  if (!data || !data.url || data.cleared) return null;
  return resolveUrl(data.url);
};

const PropertyThumb = ({
  slug,
  alt,
  aspectRatio = "4/3",
  className = "",
  imgClassName = "object-cover w-full h-full",
  priority = false,
  testId,
  // Optional widget-scoped CMS slot (e.g. "booking-widget.thumb.hotel-riad-xaluca").
  // When set, its cached URL takes precedence over the hotel-hero slot,
  // and the underlying <img> is tagged with `data-cms-slot=<cmsSlot>`
  // so AutoImageEditor persists new edits to the widget-global slot.
  cmsSlot = null,
}) => {
  const hotel = HOTELS.find((h) => h.slug === slug) || null;
  const heroSlot = `hotel/${slug}/about-hotel`;
  const [heroData, setHeroData] = useState(() => getCachedImage(heroSlot));
  const [widgetUrl, setWidgetUrl] = useState(() => readSlot(cmsSlot));

  // Subscribe to the hero slot (the historical default).
  useEffect(() => {
    ensureImageLoaded();
    const unsub = subscribeImage(heroSlot, (next) => setHeroData(next ? { ...next } : next));
    return unsub;
  }, [heroSlot]);

  // Subscribe to the widget-scoped slot when provided. Kept in a
  // separate state so we can privilege it over the hero slot with a
  // clean fallback order at render time.
  useEffect(() => {
    if (!cmsSlot) {
      setWidgetUrl(null);
      return undefined;
    }
    ensureImageLoaded();
    setWidgetUrl(readSlot(cmsSlot));
    const unsub = subscribeImage(cmsSlot, (next) => {
      if (!next || next.cleared || !next.url) setWidgetUrl(null);
      else setWidgetUrl(resolveUrl(next.url));
    });
    return unsub;
  }, [cmsSlot]);

  const heroCleared = !!heroData?.cleared;
  const heroUrl = heroCleared ? null : resolveUrl(heroData?.url);
  const src = widgetUrl || heroUrl || hotel?.image || null;
  const finalAlt = alt || hotel?.name || slug;

  // Pass-through props that let AutoImageEditor persist edits to the
  // widget-global slot instead of a page-scoped auto-generated one.
  const imgAttrs = cmsSlot ? { "data-cms-slot": cmsSlot } : {};

  return (
    <span
      className={`block overflow-hidden bg-[#1A1513]/8 ${className}`}
      data-testid={testId || `property-thumb-${slug}`}
    >
      <SmartImage
        src={src}
        fallback={hotel?.image || null}
        alt={finalAlt}
        aspectRatio={aspectRatio}
        priority={priority}
        imgClassName={imgClassName}
        {...imgAttrs}
      />
    </span>
  );
};

export default PropertyThumb;
